import React, { useState, useMemo } from 'react';
import { Modal, Form, Input, Select, Button, message, TreeSelect } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_USER, CreateUserMutationVariables, CreateUserMutationResponse } from './Users';
import {
  SpaceOption,
  SpaceRelation,
  ASSIGN_USER_TO_SPACE,
  AssignUserToSpaceVariables,
  AssignUserToSpaceResponse,
  AUTH_API_URL,
} from './SpaceAssignments';
import { getSpaceLevelIcon } from './SpaceIcons';
import { GET_SITE_HIERARCHY } from '@/hooks/useSiteOptions';
import { SiteOption } from '@/hooks/useSiteHierarchyFilters';

const { Option } = Select;

interface TreeNode {
  title: string;
  value: string;
  key: string;
  icon: React.ReactNode;
  children?: TreeNode[];
}

interface InviteUserModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

// Build tree data from flat space options with icons
const buildTreeData = (spaces: SpaceOption[]): TreeNode[] => {
  const nodeMap = new Map<string, TreeNode>();
  const rootNodes: TreeNode[] = [];

  // Create nodes for all spaces
  spaces.forEach((space) => {
    const icon = getSpaceLevelIcon(space.level);
    nodeMap.set(space.id, {
      title: space.name,
      value: space.id,
      key: space.id,
      icon: icon,
      children: [],
    });
  });

  // Build tree structure
  spaces.forEach((space) => {
    const node = nodeMap.get(space.id);
    if (!node) return;
    if (space.parentId) {
      const parent = nodeMap.get(space.parentId);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(node);
      } else {
        rootNodes.push(node);
      }
    } else {
      rootNodes.push(node);
    }
  });

  // Remove empty children arrays for leaf nodes
  const cleanNode = (node: TreeNode): TreeNode => {
    if (node.children && node.children.length === 0) {
      const { children: _, ...rest } = node;
      return rest as TreeNode;
    }
    if (node.children) {
      return { ...node, children: node.children.map(cleanNode) };
    }
    return node;
  };

  return rootNodes.map(cleanNode);
};

// Flatten site hierarchy into SpaceOption[] for the tree
function flattenHierarchyToSpaceOptions(sites: SiteOption[]): SpaceOption[] {
  const options: SpaceOption[] = [];
  for (const site of sites) {
    options.push({ id: site.id, name: site.name, level: 'site' });
    for (const building of site.buildings || []) {
      options.push({ id: building.id, name: building.name, level: 'building', parentId: site.id });
      for (const floor of building.floors || []) {
        options.push({ id: floor.id, name: floor.name, level: 'floor', parentId: building.id });
        for (const room of floor.rooms || []) {
          options.push({ id: room.id, name: room.name, level: 'room', parentId: floor.id });
        }
        for (const zone of floor.zones || []) {
          options.push({ id: zone.id, name: zone.name, level: 'zone', parentId: floor.id });
        }
      }
    }
  }
  return options;
}

export const InviteUserModal: React.FC<InviteUserModalProps> = ({ open, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [selectedSpaces, setSelectedSpaces] = useState<string[]>([]);
  const [relation, setRelation] = useState<SpaceRelation>('can_view');
  const [spaceError, setSpaceError] = useState<string | null>(null);

  // Fetch real site hierarchy from main Butlr API
  const { data: hierarchyData } = useQuery(GET_SITE_HIERARCHY, {
    skip: !open,
  });

  const availableSpaces = useMemo(() => {
    const sites = (hierarchyData?.sites?.data || []) as SiteOption[];
    return flattenHierarchyToSpaceOptions(sites);
  }, [hierarchyData]);

  const treeData = useMemo(() => buildTreeData(availableSpaces), [availableSpaces]);

  const [createUser, { loading: isCreatingUser }] = useMutation<
    CreateUserMutationResponse,
    CreateUserMutationVariables
  >(CREATE_USER, {
    context: { uri: AUTH_API_URL },
  });

  const [assignToSpace, { loading: isAssigning }] = useMutation<
    AssignUserToSpaceResponse,
    AssignUserToSpaceVariables
  >(ASSIGN_USER_TO_SPACE, {
    context: { uri: AUTH_API_URL },
  });

  const isLoading = isCreatingUser || isAssigning;

  const validateSpaceAssignment = (): boolean => {
    if (selectedSpaces.length === 0) {
      setSpaceError('Please assign at least one space');
      return false;
    }
    setSpaceError(null);
    return true;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!validateSpaceAssignment()) {
        return;
      }

      // Step 1: Create user via auth API
      const roles = values.role === 'admin' ? ['admin'] : ['member'];
      const name = values.email.split('@')[0];
      const result = await createUser({
        variables: {
          input: {
            email: values.email,
            name,
            password: crypto.randomUUID(),
            roles,
          },
        },
      });

      const userId = result.data?.createUser?.id;
      if (!userId) {
        message.error('Failed to create user: no user ID returned');
        return;
      }

      // Step 2: Assign selected spaces to the new user
      const failedSpaces: string[] = [];
      for (const spaceId of selectedSpaces) {
        try {
          await assignToSpace({
            variables: {
              input: {
                user_id: userId,
                space_type: availableSpaces.find((s) => s.id === spaceId)?.level || 'room',
                space_id: spaceId,
                relation,
              },
            },
          });
        } catch (err) {
          console.error('Failed to create space assignment:', err);
          failedSpaces.push(spaceId);
        }
      }

      if (failedSpaces.length > 0) {
        message.warning(
          `Member created but ${failedSpaces.length} of ${selectedSpaces.length} space assignment(s) failed.`,
        );
      } else {
        message.success('Member added successfully!');
      }
      handleClose();
      onSuccess();
    } catch (error: unknown) {
      console.error('Error adding member:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add member';
      message.error(errorMessage);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedSpaces([]);
    setRelation('can_view');
    setSpaceError(null);
    onCancel();
  };

  const handleSpaceChange = (values: string[]) => {
    setSelectedSpaces(values);
    if (values.length > 0) {
      setSpaceError(null);
    }
  };

  return (
    <Modal
      title="Add New Member"
      open={open}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isLoading}
          onClick={handleSubmit}
          className="bg-black hover:bg-gray-800"
        >
          Add
        </Button>,
      ]}
      width={520}
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 4 }}
        wrapperCol={{ span: 12 }}
        className="mt-4"
        colon
      >
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: 'Please enter a valid email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
          className="mb-3"
        >
          <Input placeholder="name@company.com" />
        </Form.Item>

        <Form.Item
          label="Role"
          name="role"
          rules={[{ required: true, message: 'Please select a role' }]}
          initialValue="member"
          className="mb-3"
        >
          <Select>
            <Option value="member">Team Member</Option>
            <Option value="admin">Admin</Option>
          </Select>
        </Form.Item>

        <div className="mb-4 bg-gray-50 rounded-lg p-4 mt-4" style={{ marginLeft: 0 }}>
          <div className="mb-2 text-sm font-medium">Assign Space:</div>
          <div className="flex items-center gap-2">
            <TreeSelect
              treeData={treeData}
              value={selectedSpaces}
              onChange={handleSpaceChange}
              placeholder="Please select a space"
              treeDefaultExpandAll
              multiple
              showSearch={false}
              className="flex-1"
              maxTagCount={3}
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
              status={spaceError ? 'error' : undefined}
              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
              treeLine={{ showLeafIcon: false }}
              treeIcon
            />
            <Select
              value={relation}
              onChange={(value) => setRelation(value)}
              style={{ width: 120 }}
            >
              <Option value="can_view">Can View</Option>
              <Option value="can_edit">Can Edit</Option>
            </Select>
          </div>
          {spaceError && <div className="text-red-500 text-xs mt-1">{spaceError}</div>}
        </div>
      </Form>
    </Modal>
  );
};

export default InviteUserModal;
